import { useMemo, useEffect, useCallback, useRef } from 'react';
import { useGetInventoryVMInfoQuery, useGetInventorySummaryQuery } from '../api/discovery/inventory';
import { DISCOVERY_ROUTE_CONFIG } from '../constants/routes';

export const NOTIFICATION_IDS = {
    UNASSIGNED_VMS: 'unassigned_vms',
    OPTIMIZATIONS_NEEDED: 'optimizations_needed',
};

const buildActiveNotificationDefs = ({ unassignedVMsData, summaryData }) => {
    const defs = [];

    const unassignedCount = unassignedVMsData?.page?.totalElements ?? 0;
    if (unassignedCount > 0) {
        defs.push({
            id: NOTIFICATION_IDS.UNASSIGNED_VMS,
            notification_type: 'action',
            title: 'Action Required: Unassigned Virtual Machines',
            description:
                'Some virtual machines are not currently assigned to a Move Group or Application. Assign them to continue organizing your migration plan.',
            link: {
                route: DISCOVERY_ROUTE_CONFIG.moveGroupsAndApps.path,
                routeState: { view: 'unassigned' },
                text: 'Assign Now',
            },
        });
    }

    const hasOptimizations =
        summaryData &&
        (summaryData.undersized_by_cpu > 0 ||
            summaryData.oversized_by_cpu > 0 ||
            summaryData.undersized_by_ram > 0 ||
            summaryData.oversized_by_ram > 0);
    if (hasOptimizations) {
        defs.push({
            id: NOTIFICATION_IDS.OPTIMIZATIONS_NEEDED,
            notification_type: 'action',
            title: 'Action Required: Optimizations Needed',
            description:
                'One or more machines still need optimization before they can proceed through the migration workflow.',
            link: {
                route: DISCOVERY_ROUTE_CONFIG.inventory.path,
                routeState: { filter: 'needs_optimization' },
                text: 'View Inventory',
            },
        });
    }

    return defs;
};

export const useNotifications = ({
    orgId,
    notificationItems,
    saveNotificationItems,
    isCustomizationLoading,
    pollingInterval,
}) => {
    const skipQueries = !orgId || isCustomizationLoading;
    const queriesActive = !skipQueries && !!orgId;

    const {
        data: unassignedVMsData,
        isLoading: unassignedLoading,
        refetch: refetchUnassigned,
    } = useGetInventoryVMInfoQuery(
        { orgId, size: 1, page: 0, unassigned: true },
        { skip: skipQueries, pollingInterval, refetchOnMountOrArgChange: true },
    );

    const {
        data: summaryData,
        isLoading: summaryLoading,
        refetch: refetchSummary,
    } = useGetInventorySummaryQuery(orgId, {
        skip: skipQueries,
        pollingInterval,
        refetchOnMountOrArgChange: true,
    });

    const isDataLoading = queriesActive && (unassignedLoading || summaryLoading);
    const isLoading = !orgId || isCustomizationLoading || isDataLoading;

    // Determine notifications based on query results
    const activeNotificationDefs = useMemo(() => {
        if (!queriesActive || isDataLoading) return [];
        return buildActiveNotificationDefs({ unassignedVMsData, summaryData });
    }, [queriesActive, isDataLoading, unassignedVMsData, summaryData]);

    const notificationItemsRef = useRef(notificationItems);
    useEffect(() => {
        notificationItemsRef.current = notificationItems;
    }, [notificationItems]);

    // Sync effect for only when queries to user-customizations have completed
    useEffect(() => {
        if (!queriesActive || isDataLoading || isCustomizationLoading) return;

        const currentItems = notificationItemsRef.current;
        const now = new Date().toISOString();
        let changed = false;
        const updatedItems = { ...currentItems };
        const activeIds = new Set(activeNotificationDefs.map((n) => n.id));

        Object.keys(updatedItems).forEach((id) => {
            if (!activeIds.has(id) && updatedItems[id].hidden !== true) {
                updatedItems[id] = {
                    ...updatedItems[id],
                    hidden: true,
                    date_hidden: now,
                    date_modified: now,
                };
                changed = true;
            }
        });

        activeNotificationDefs.forEach((n) => {
            const existing = updatedItems[n.id];
            const fullFields = {
                notification_type: n.notification_type,
                title: n.title,
                description: n.description,
                link_text: n.link?.text,
                link_route: n.link?.route,
                link_route_state: n.link?.routeState,
            };

            if (!existing) {
                updatedItems[n.id] = {
                    ...fullFields,
                    hidden: false,
                    date_hidden: null,
                    date_modified: now,
                };
                changed = true;
            } else if (existing.hidden === true || existing.title === undefined) {
                updatedItems[n.id] = {
                    ...existing,
                    ...fullFields,
                    hidden: false,
                    date_hidden: null,
                    date_modified: now,
                };
                changed = true;
            }
        });

        if (changed) {
            saveNotificationItems(updatedItems);
        }
    }, [activeNotificationDefs, isCustomizationLoading, isDataLoading, queriesActive, saveNotificationItems]);

    // Notifications derived from persisted items (session storage / user_customizations).
    const notifications = useMemo(() => {
        return Object.entries(notificationItems)
            .filter(([, item]) => !item.hidden)
            .map(([id, item]) => ({
                id,
                notification_type: item.notification_type,
                title: item.title,
                description: item.description,
                link: item.link_route
                    ? {
                          route: item.link_route,
                          routeState: item.link_route_state,
                          text: item.link_text,
                      }
                    : null,
            }));
    }, [notificationItems]);

    // Trigger a fresh re-query
    const refreshNotifications = useCallback(() => {
        if (queriesActive) {
            refetchUnassigned();
            refetchSummary();
        }
    }, [queriesActive, refetchUnassigned, refetchSummary]);

    return { notifications, isLoading, refreshNotifications };
};
